<script lang="ts">
    import { createEventDispatcher } from 'svelte'
    const dispatch = createEventDispatcher()

    import { GridColTypeEnum, GridItemShow, GridSlot, type GridCol } from './zgrid'
    import Fa from 'svelte-fa'
    import dayjs from 'dayjs'
    import Zinput from './zinput.svelte'

    export let col: GridCol
    export let slot: GridSlot
    export let isMobile
    export let defaultEmptyString = '-'
    // só usado por col.type === checkbox — o estado marcado/desmarcado vive na linha (GridLine),
    // não no slot: slot.content aqui é o valor de negócio (ex.: o id) que volta no callback das
    // bulkActions, não um boolean de "marcado".
    export let checked = false

    function onLineClicked(i) {
        dispatch('click', i)
    }

    function evaluateContent(col: GridCol, slot: GridSlot) {
        if (col.type === GridColTypeEnum.date) {
            if (!slot.content) return defaultEmptyString
            if (isMobile) return dayjs(slot.content).format('DD/MM/YY')
            return dayjs(slot.content).format('DD/MM/YYYY')
        }
        if (col.type === GridColTypeEnum.time) {
            if (!slot.content) return defaultEmptyString
            return dayjs(slot.content).format('HH:mm:ss')
        }
        if (col.type === GridColTypeEnum.time_short) {
            if (!slot.content) return defaultEmptyString
            return dayjs(slot.content).format('HH:mm')
        }
        if (col.type === GridColTypeEnum.datetime) {
            if (!slot.content) return defaultEmptyString
            if (isMobile) return dayjs(slot.content).format('DD/MM/YY HH:mm')
            return dayjs(slot.content).format('DD/MM/YYYY HH:mm:ss')
        }
        if (col.type === GridColTypeEnum.datetime_friendly) {
            if (!slot.content) return defaultEmptyString
            return window.api.utils.utils.dateFriendly(slot.content)
            //return friendlyDate(slot.content)
        }
        if (col.type === GridColTypeEnum.string) {
            if (!slot.content) return defaultEmptyString
            if (col.maxLength > 0 && slot.content.length > col.maxLength) {
                return String(slot.content).substring(0, col.maxLength - 3) + '...'
            }
            return slot.content
        }
        if (col.type === GridColTypeEnum.int) {
            if (slot.content === null || slot.content === undefined) return defaultEmptyString
            return slot.content.toString()
        }
        if (col.type === GridColTypeEnum.float) {
            if (slot.content === null || slot.content === undefined) return defaultEmptyString
            return slot.content.toString()
        }
        if (col.type === GridColTypeEnum.percent) {
            if (slot.content === null || slot.content === undefined) return defaultEmptyString
            if (slot.content > 1000) return '1000%+'
            return parseInt(slot.content).toString() + '%'
        }
        if (col.type === GridColTypeEnum.float_2) {
            if (slot.content === null || slot.content === undefined) return defaultEmptyString
            return slot.content.toFixed(2)
        }
        if (col.type === GridColTypeEnum.phone) {
            if (slot.content === null || slot.content === undefined) return defaultEmptyString
            return window.api.utils.utils.phoneFriendly(slot.content)
        }
        if (col.type === GridColTypeEnum.email) {
            if (!slot.content) return defaultEmptyString
            return slot.content.toString().toLowerCase().trim()
        }
        return slot.content.toString()
    }
</script>

{#if slot.content === null}
    <div>{defaultEmptyString}</div>
{:else if col.type === GridColTypeEnum.button}
    {#each slot.content as button}
        {#if button}
            {#if !isMobile || button.mobile === GridItemShow.show}
                <button
                    disabled={button.disabled}
                    class="btn btn-sm btn-custom ms-2 {button.class || ''}"
                    on:click={button.callback}
                    title={button.title || ''}
                >
                    {#if button.icon}
                        <Fa class="button-icon" icon={button.icon} />
                    {/if}
                    {button.label}
                </button>
            {/if}
        {/if}
    {/each}
{:else if col.type === GridColTypeEnum.zinput}
    <Zinput
        type={slot.content.type}
        class={slot.content.class}
        bind:value={slot.content.value}
        options={slot.content.options}
        on:change={() => (slot.content.callback ? slot.content.callback(slot.content.value) : null)}
        disabled={slot.content.disabled}
        label={slot.content.label}
        help={slot.content.help}
        placeholder={slot.content.placeholder || ''}
    ></Zinput>
{:else if col.type === GridColTypeEnum.toggle}
    {#if !isMobile || slot.content.mobile === GridItemShow.show}
        <div class="form-check form-switch {slot.content.class}">
            <input
                disabled={slot.content.disabled}
                class="form-check-input"
                bind:checked={slot.content.checked}
                on:change={() => (slot.content.callback ? slot.content.callback(slot.content.checked) : null)}
                type="checkbox"
                role="switch"
            />
            {#if slot.content.label}
                <label for="">{slot.content.label}</label>
            {/if}
        </div>
    {/if}
{:else if col.type === GridColTypeEnum.checkbox}
    <!-- além do bind:checked (que já atualiza line.checked no pai), dispara "change" explicitamente:
         o checkbox "selecionar tudo" do cabeçalho fica numa parte separada do template (outro
         {#each}), e só recalcula/reaplica seu próprio checked de verdade quando "grid" é reatribuído
         de propósito (grid = grid) — o bind: sozinho, através de dois componentes, não garante isso. -->
    <input class="form-check-input zgrid_checkbox" bind:checked type="checkbox" on:change={() => dispatch('change')} />
{:else if col.type === GridColTypeEnum.badge}
    {#if Array.isArray(slot.content)}
        {#each slot.content as badge}
            <span class="zbadge_default {badge.class || ''}" style="background-color:{badge.color}"><span>{badge?.label}</span></span>
        {/each}
    {:else}
        <span class="zbadge_default {slot.content.class || ''}" style="background-color:{slot.content.color}"><span>{slot.content?.label}</span></span>
    {/if}
{:else}
    <div>
        {evaluateContent(col, slot)}
        {#if slot.badge}
            <span class="zbadge_default {slot.badge.class || ''}" style="background-color:{slot.badge?.color}"><span>{slot.badge?.label}</span></span>
        {/if}
        {#if slot.action}
            <span class="zgrid_slot_action {slot.action.class || ''}" title={slot.action.title || ''} on:click={slot.action.callback}>
                {#if slot.action.icon}<Fa icon={slot.action.icon} />{/if}
                {slot.action.label}
            </span>
        {/if}
    </div>
{/if}

<style>
    .zgrid_slot_action {
        display: inline-block;
        cursor: pointer;
        padding-left: 4px;
    }
</style>
